<template>
  <div
    class="flex flex-col items-center justify-center flex-1 p-6 bg-gradient-to-br from-blue-50 to-white w-full"
  >
    <!-- Hero Section -->
    <div class="w-full max-w-3xl text-center mb-10" data-aos="fade-up">
      <h1 class="text-4xl md:text-5xl font-extrabold text-blue-800 mb-4">
        Welcome to guest5of5
      </h1>
      <p class="text-lg text-gray-700 mb-4">
        guest5of5 is your all-in-one platform for seamless hotel booking, pre-checkin, and
        guest experience management. Whether you're traveling for business or leisure,
        guest5of5 ensures a smooth, personalized, and memorable stay from start to finish.
      </p>
      <router-link
        to="/hotels"
        class="btn btn-primary px-8 py-3 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition text-lg font-semibold"
        >Browse Hotels</router-link
      >
    </div>

    <!-- Booking Search Section -->
    <div class="w-full max-w-3xl bg-white/90 rounded-2xl shadow-lg p-6 mb-10">
      <form class="flex flex-col gap-4" @submit.prevent="onSearch">
        <!-- Location Selector (API City Autocomplete) -->
        <div class="mb-2">
          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-geo-alt"></i>
            </span>
            <input
              v-model="search.city"
              class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-300 transition shadow-sm"
              placeholder="Enter city or location"
              required
              @input="searchCities"
              @focus="searchCities"
              @blur="handleCityBlur"
            />

            <div
              v-if="showCityDropdown && cities.length"
              class="absolute z-10 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-auto"
            >
              <div v-if="cityLoading" class="p-2 text-gray-500 text-center">
                Loading...
              </div>
              <div v-else>
                <div
                  v-for="city in cities"
                  :key="city.id"
                  @mousedown.prevent="selectCity(city)"
                  class="px-4 py-2 hover:bg-blue-100 cursor-pointer"
                >
                  {{ city.name }}
                  <span v-if="city.state">, {{ city.state.name }}</span>
                  <span v-if="city.state && city.state.country"
                    >, {{ city.state.country.name }}</span
                  >
                </div>
              </div>
            </div>
            <span
              class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
            >
              <i class="bi bi-chevron-down"></i>
            </span>
          </div>
        </div>

        <!-- Second Line: Check-in, Check-out, Adults, Children, Search -->
        <div class="flex flex-col md:flex-row gap-3 items-center w-full mt-2">
          <!-- Date Picker -->
          <div class="flex-1 w-full">
            <div class="relative">
              <span
                class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
              >
                <i class="bi bi-calendar-date"></i>
              </span>
              <flat-pickr
                v-model="dateRange"
                :config="dateOptions"
                class="w-full border border-gray-200 rounded-lg h-12 px-4 focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-300 transition shadow-sm"
                placeholder="Select check-in and check-out"
              />
            </div>
          </div>
          <!-- Adults -->
          <div class="flex-shrink-0 w-full md:w-auto">
            <div class="relative">
              <span
                class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
              >
                <i class="bi bi-people"></i>
              </span>
              <select
                v-model="search.adults"
                class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full md:w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-400 transition shadow-sm appearance-none"
                required
              >
                <option disabled value="">Adult</option>
                <option v-for="n in 5" :key="n" :value="n">
                  {{ n }} Adult{{ n > 1 ? "s" : "" }}
                </option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
              >
                <i class="bi bi-chevron-down"></i>
              </span>
            </div>
          </div>
          <!-- Children -->
          <div class="flex-shrink-0 w-full md:w-auto">
            <div class="relative">
              <span
                class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
              >
                <i class="bi bi-person-badge"></i>
              </span>
              <select
                v-model="search.children"
                class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full md:w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-400 transition shadow-sm appearance-none"
              >
                <option disabled value="">Child</option>
                <option v-for="n in 5" :key="n" :value="n">
                  {{ n }} Child{{ n > 1 ? "ren" : "" }}
                </option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
              >
                <i class="bi bi-chevron-down"></i>
              </span>
            </div>
          </div>
          <!-- Search Button -->
          <div class="flex-shrink-0 w-full md:w-auto">
            <button
              type="submit"
              class="h-12 w-full md:w-auto bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-lg px-6 font-bold shadow-lg transition flex items-center justify-center"
            >
              <i class="bi bi-search mr-2"></i>Search
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Features Section -->
    <div class="w-full px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
      <div
        class="bg-white rounded-lg shadow p-6 flex flex-col items-center"
        data-aos="zoom-in"
      >
        <i class="bi bi-calendar2-check text-blue-600 text-3xl mb-2"></i>
        <h3 class="font-bold text-lg mb-2">Effortless Booking</h3>
        <p class="text-gray-600 text-center">
          Book hotels in seconds with real-time availability and instant confirmation.
        </p>
      </div>
      <div
        class="bg-white rounded-lg shadow p-6 flex flex-col items-center"
        data-aos="zoom-in"
        data-aos-delay="200"
      >
        <i class="bi bi-person-badge text-blue-600 text-3xl mb-2"></i>
        <h3 class="font-bold text-lg mb-2">Pre-checkin & Personalization</h3>
        <p class="text-gray-600 text-center">
          Complete your check-in before arrival and set your preferences for a tailored
          stay.
        </p>
      </div>
      <div
        class="bg-white rounded-lg shadow p-6 flex flex-col items-center"
        data-aos="zoom-in"
        data-aos-delay="400"
      >
        <i class="bi bi-headset text-blue-600 text-3xl mb-2"></i>
        <h3 class="font-bold text-lg mb-2">24/7 Guest Support</h3>
        <p class="text-gray-600 text-center">
          Get help anytime with our dedicated support and real-time notifications.
        </p>
      </div>
    </div>

    <!-- How It Works Section -->
    <div class="w-full max-w-5xl mb-12" data-aos="fade-up">
      <h2 class="text-2xl font-bold text-blue-700 mb-4 text-center">How It Works</h2>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div class="flex flex-col items-center">
          <i class="bi bi-search text-blue-600 text-3xl mb-2"></i>
          <h4 class="font-semibold mb-1">1. Search</h4>
          <p class="text-gray-600 text-center text-sm">
            Find the perfect hotel from our curated list of properties worldwide.
          </p>
        </div>
        <div class="flex flex-col items-center">
          <i class="bi bi-bookmark-check text-blue-600 text-3xl mb-2"></i>
          <h4 class="font-semibold mb-1">2. Book</h4>
          <p class="text-gray-600 text-center text-sm">
            Reserve your room instantly with secure payment options.
          </p>
        </div>
        <div class="flex flex-col items-center">
          <i class="bi bi-clipboard-check text-blue-600 text-3xl mb-2"></i>
          <h4 class="font-semibold mb-1">3. Pre-checkin</h4>
          <p class="text-gray-600 text-center text-sm">
            Complete your check-in online and set your preferences before arrival.
          </p>
        </div>
        <div class="flex flex-col items-center">
          <i class="bi bi-emoji-smile text-blue-600 text-3xl mb-2"></i>
          <h4 class="font-semibold mb-1">4. Enjoy</h4>
          <p class="text-gray-600 text-center text-sm">
            Experience a smooth, personalized stay with guest5of5 support at every step.
          </p>
        </div>
      </div>
    </div>

    <!-- Why Choose guest5of5 Section -->
    <div class="w-full px-4 md:px-8 mb-12">
      <h2 class="text-2xl font-bold text-blue-700 mb-4 text-center">
        Why Choose guest5of5?
      </h2>
      <ul class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <li class="flex items-start gap-3">
          <i class="bi bi-shield-check text-green-600 text-2xl"></i>
          <span
            ><b>Secure & Reliable:</b> Your data and payments are protected with
            industry-leading security.</span
          >
        </li>
        <li class="flex items-start gap-3">
          <i class="bi bi-lightning-charge text-yellow-500 text-2xl"></i>
          <span
            ><b>Instant Confirmation:</b> Get booking confirmation and updates in real
            time.</span
          >
        </li>
        <li class="flex items-start gap-3">
          <i class="bi bi-stars text-purple-600 text-2xl"></i>
          <span
            ><b>Top-rated Properties:</b> Choose from highly rated hotels with verified
            guest reviews.</span
          >
        </li>
        <li class="flex items-start gap-3">
          <i class="bi bi-globe2 text-blue-500 text-2xl"></i>
          <span
            ><b>Global Reach:</b> Book hotels in major cities and hidden gems around the
            world.</span
          >
        </li>
      </ul>
    </div>

    <!-- Hotel Gallery Section -->
    <div class="w-full px-4 md:px-8 mb-8" data-aos="fade-up">
      <h2 class="text-2xl font-bold text-blue-700 mb-4 text-center">Featured Hotels</h2>
      <div v-if="loading" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
      <div v-else-if="featuredHotels.length === 0" class="text-center py-8 text-gray-500">
        <i class="bi bi-building text-4xl mb-4"></i>
        <p>No featured hotels available at the moment.</p>
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          v-for="(hotel, index) in featuredHotels"
          :key="hotel.id"
          class="rounded-lg overflow-hidden shadow-lg bg-white flex flex-col items-center hover:shadow-xl transition-shadow duration-300"
          data-aos="zoom-in"
          :data-aos-delay="index * 100"
        >
        <router-link :to="{name: 'HotelDetail', params:{'id':hotel.id}}" class="w-full">
          <img
            :src="hotel.image"
            :alt="hotel.name"
            class="h-64 object-cover w-full"
            @error="handleImageError"
          />
        </router-link>
          <div class="p-4 w-full">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-semibold text-lg">{{ hotel.name }}</h4>
              <div class="flex items-center">
                <i class="bi bi-star-fill text-yellow-400 mr-1"></i>
                <span class="text-sm font-medium">{{ hotel.rating }}</span>
              </div>
            </div>
            <p class="text-gray-600 text-sm mb-2">
              {{
                hotel.description ||
                "Experience luxury and comfort at this amazing property."
              }}
            </p>
            <div class="flex items-center text-sm text-gray-500 mb-2">
              <i class="bi bi-geo-alt mr-1"></i>
              <span>{{ hotel.address }}</span>
            </div>
            <div class="flex items-center justify-between text-sm text-gray-500">
              <span
                ><i class="bi bi-clock mr-1"></i>Check-in: {{ hotel.check_in_time }}</span
              >
              <span
                ><i class="bi bi-clock mr-1"></i>Check-out:
                {{ hotel.check_out_time }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Testimonials Section -->
    <div class="w-full max-w-4xl mb-12">
      <h2 class="text-2xl font-bold text-blue-700 mb-4 text-center">
        What Our Guests Say
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="bg-white rounded-lg shadow p-6 flex flex-col items-center">
          <i class="bi bi-person-circle text-blue-600 text-3xl mb-2"></i>
          <p class="text-gray-700 italic mb-2">
            "Booking and check-in was so easy! guest5of5 made my business trip
            stress-free."
          </p>
          <span class="font-semibold text-blue-800">— Priya S.</span>
        </div>
        <div class="bg-white rounded-lg shadow p-6 flex flex-col items-center">
          <i class="bi bi-person-circle text-blue-600 text-3xl mb-2"></i>
          <p class="text-gray-700 italic mb-2">
            "I loved the personalized room setup. The support team was always available!"
          </p>
          <span class="font-semibold text-blue-800">— John D.</span>
        </div>
        <div class="bg-white rounded-lg shadow p-6 flex flex-col items-center">
          <i class="bi bi-person-circle text-blue-600 text-3xl mb-2"></i>
          <p class="text-gray-700 italic mb-2">
            "Best hotel booking experience ever. Highly recommend guest5of5!"
          </p>
          <span class="font-semibold text-blue-800">— Aisha K.</span>
        </div>
      </div>
    </div>

    <!-- Call to Action -->
    <div class="w-full px-4 md:px-8 text-center mt-8">
      <h3 class="text-xl font-bold text-blue-800 mb-2">
        Ready to experience the best in hotel booking?
      </h3>
      <router-link
        to="/hotels"
        class="btn btn-primary px-8 py-3 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition text-lg font-semibold"
        >Find Your Stay with guest5of5</router-link
      >
    </div>
  </div>
</template>

<script>
import hotel1 from "@/assets/hotel1.jpg";
import hotel2 from "@/assets/hotel2.jpg";
import hotel3 from "@/assets/hotel3.jpg";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { request } from "@/Util/Request";
import { extractLocation } from "@/Util/locationParser";
import AOS from "aos";
import "aos/dist/aos.css";

export default {
  name: "UserHome",
  components: {
    FlatPickr,
  },
  data() {
    return {
      hotel1,
      hotel2,
      hotel3,
      search: {
        checkin: "",
        checkout: "",
        adults: "",
        children: "",
        city: "",
      },
      dateRange: [], // Will be [checkin, checkout]
      dateOptions: {
        mode: "range",
        dateFormat: "d M Y",
        altInput: true,
        altFormat: "d M Y",
        allowInput: true,
        showMonths: 2,
        minDate: "today",
        wrap: true,
        onReady(selectedDates, dateStr, instance) {
          if (instance.altInput) {
            instance.altInput.setAttribute("readonly", "readonly");
          }
        },
        onOpen(selectedDates, dateStr, instance) {
          if (instance.altInput) {
            instance.altInput.setAttribute("readonly", "readonly");
          }
        },
        onClose(selectedDates, dateStr, instance) {
          if (selectedDates.length === 1) {
            const checkin = selectedDates[0];
            const tomorrow = new Date(checkin);
            tomorrow.setDate(checkin.getDate() + 1);

            // Auto set checkout as tomorrow
            instance.setDate([checkin, tomorrow], true);
          }
        },
      },
      featuredHotels: [],
      loading: false,
      cities: [],
      showCityDropdown: false,
      cityLoading: false,
      selectedCity: null, // Store selected city object
    };
  },
  watch: {
    dateRange(newVal) {
      if (typeof newVal === "string" && newVal.includes(" to ")) {
        const [checkin, checkout] = newVal.split(" to ");
        this.search.checkin = checkin;
        this.search.checkout = checkout;
      }
    },
  },
  async mounted() {
    await Promise.all([
      this.fetchFeaturedHotels(),
      // this.initGoogleAutocomplete() // Removed as per edit
    ]);
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
    });

    // Keep your existing mounted code
    this.fetchFeaturedHotels();
  },
  methods: {
    onSearch() {
      if (!this.selectedCity) {
        this.$bvToast.toast("Please select a valid city from the list.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      const { city } = extractLocation(this.selectedCity.name);
      const cityId = this.selectedCity.id;

      this.$router.push({
        name: "UserHotelAvailability",
        query: {
          city,
          cityId,
          cityName: city,
          checkin: this.search.checkin,
          checkout: this.search.checkout,
          adults: this.search.adults,
          children: this.search.children,
        },
      });
    },
    async fetchFeaturedHotels() {
      this.loading = true;
      try {
        const response = await request({ method: "get", url: "/public/hotel-groups" });
        if (response.success) {
          // Extract all hotels from all hotel groups
          const allHotels = [];
          response.data.forEach((group) => {
            if (group.hotels && group.hotels.length > 0) {
              group.hotels.forEach((hotel) => {
                // Only include hotels that have at least one gallery image
                if (hotel.galleries && hotel.galleries.length > 0) {
                  allHotels.push({
                    ...hotel,
                    image: hotel.galleries[0].path, // Use the first gallery image
                  });
                }
              });
            }
          });
          this.featuredHotels = allHotels;
        }
      } catch (error) {
        console.error("Error fetching featured hotels:", error);
        this.featuredHotels = [];
      } finally {
        this.loading = false;
      }
    },
    async searchCities() {
      const query = this.search.city;
      if (query.length >= 2) {
        this.cityLoading = true;
        try {
          const response = await request({
            method: "get",
            url: `/citiesbyhotel?search=${encodeURIComponent(query)}`,
          });
          if (response.data) {
            this.cities = response.data;
            this.showCityDropdown = true;
          } else {
            this.cities = [];
            this.showCityDropdown = false;
          }
        } catch (error) {
          this.cities = [];
          this.showCityDropdown = false;
        } finally {
          this.cityLoading = false;
        }
      } else {
        this.cities = [];
        this.showCityDropdown = false;
      }
    },
    selectCity(cityObj) {
      this.search.city = cityObj.name;
      this.selectedCity = cityObj;
      this.showCityDropdown = false;
      this.cities = [];
    },
    handleCityBlur() {
      setTimeout(() => {
        this.showCityDropdown = false;
        if (!this.selectedCity || this.search.city !== this.selectedCity.name) {
          this.search.city = "";
          this.selectedCity = null;
        }
      }, 200);
    },
    handleImageError(event) {
      // Fallback to a default hotel image if the API image fails to load
      event.target.src = this.hotel1;
    },
    // Removed as per edit
    // async onStateChange() {
    //   await this.fetchCities(); // Fetch cities when state changes
    // },
    // Removed as per edit
    // async fetchStates() {
    //   this.statesLoading = true;
    //   try {
    //     // Try to load from localStorage first
    //     const cached = localStorage.getItem('states');
    //     if (cached) {
    //       this.states = JSON.parse(cached);
    //       this.statesLoading = false;
    //       return;
    //     }
    //     const response = await axios.get('/states');
    //     this.states = response.data.data;
    //     // Cache in localStorage
    //     localStorage.setItem('states', JSON.stringify(this.states));
    //   } catch (error) {
    //     console.error('Error fetching states:', error);
    //     this.states = [];
    //   } finally {
    //     this.statesLoading = false;
    //   }
    // },
    // Removed as per edit
    // initGoogleAutocomplete() {
    //   if (!window.google) {
    //     // Load Google Maps script if not already loaded
    //     const script = document.createElement('script');
    //     script.src = `https://maps.googleapis.com/maps/api/js?key=YOUR_GOOGLE_MAPS_API_KEY&libraries=places`;
    //     script.async = true;
    //     script.onload = this.setupAutocomplete;
    //     document.head.appendChild(script);
    //   } else {
    //     this.setupAutocomplete();
    //   }
    // },
    // Removed as per edit
    // setupAutocomplete() {
    //   if (!this.$refs.autocompleteInput) return;
    //   const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteInput, {
    //     types: ['(cities)']
    //   });
    //   autocomplete.addListener('place_changed', () => {
    //     const place = autocomplete.getPlace();
    //     this.search.city = place.formatted_address || place.name;
    //   });
    // }
  },
};
</script>
<style>
.px-4 {
  padding-right: 1.5rem !important;
  padding-left: 2.5rem !important;
}
</style>
